import { useState, useEffect } from 'react';
import AcornEdit from './AcornEdit';
import { Acorn } from '../../models/acorn';
import { generateCouponPDF } from './CouponPDF';
import axios from "../../axios-config";
import QRScanner from "../../components/Acorn/QRScanner";
import { Copy, Search, Download, UserPlus } from 'lucide-react';
import { toast } from 'react-toastify';
import { useOrganizations } from '../../contexts/OrganizationsContext';

interface AcornGenerator {
    OrganizationId: string;
    prefix: string;
    points: number;
    numAcorns: number;
    description: string;
}

const AcornMain: React.FC = () => {
    const [acorns, setAcorns] = useState<Acorn[]>([]);

    const [generateParams, setGenerateParams] = useState<AcornGenerator>({
        OrganizationId: '',
        prefix: '',
        points: 1,
        numAcorns: 1,
        description: '',
    });
    const [editingAcorn, setEditingAcorn] = useState<Acorn | null>(null);
    const [isReissueOpen, setIsReissueOpen] = useState(false);
    const { organizations } = useOrganizations();
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        fetchAcorns();
    }, []);

    const fetchAcorns = async () => {
        try {
            const { data } = await axios.get('/api/acorns', { withCredentials: true });
            setAcorns(data);
        } catch (error) {
            console.error('Error fetching acorns:', error);
        }
    };

    const updateAcorn = async (id: number, updatedAcorn: Partial<Acorn>) => {
        try {
            const { data } = await axios.put(`/api/acorns/${id}`, updatedAcorn, { withCredentials: true });
            setAcorns(acorns.map(acorn => (acorn.acornId === id ? data : acorn)));
            setEditingAcorn(null);
        } catch (error) {
            console.error('Error updating acorn:', error);
        }
    };

    const deleteAcorn = async (id: number) => {
        try {
            await axios.delete(`/api/acorns/${id}`, { withCredentials: true });
            setAcorns(acorns.filter(acorn => acorn.acornId !== id));
        } catch (error) {
            console.error('Error deleting acorn:', error);
        }
    };

    const reIssueAcorn = async (id: number) => {
        const selectedAcorn = acorns.find(a => a.acornId === id);
        if (!selectedAcorn) {
            console.error('Acorn not found for reissue');
            return;
        }
        try {
            await axios.post(`api/Acorns/ReIssue?acornCode=${selectedAcorn.code}`, {}, { withCredentials: true });
            fetchAcorns();
        } catch (error) {
            console.error('Error reissuing acorn:', error);
        }
    };

    // Now this function will trigger PDF generation immediately.
    const handleGenerateCouponPDF = async (acorns: [Acorn, ...Acorn[]]) => {
        try {
            await generateCouponPDF(acorns);
        } catch (error) {
            console.error('Error generating coupon PDF:', error);
        }
    };

    const generateAcorns = async () => {
        try {
            const { OrganizationId, prefix, points, numAcorns, description } = generateParams;
            const { data } = await axios.post('/api/Acorns/Generate', {
                OrganizationId,
                prefix,
                points,
                numAcorns,
                description,
            }, { withCredentials: true });
            await handleGenerateCouponPDF(data);
            setAcorns([...acorns, ...data]);
        } catch (error) {
            console.error('Error generating acorns:', error);
        }
    };

    const handleEdit = (acorn: Acorn) => {
        setEditingAcorn(acorn);
    };

    const handleSave = (updatedAcorn: Acorn) => {
        updateAcorn(updatedAcorn.acornId, updatedAcorn);
    };

    const handleCancel = () => {
        setEditingAcorn(null);
    };

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6 text-center">Coupons</h1>
            <button
                onClick={generateAcorns}
                className="bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded mb-6"
            >
                Generate Coupons
            </button>
            <button
                onClick={() => setIsReissueOpen(true)}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded"
            >
                Reissue Coupons
            </button>
            {/* Updated Search Section */}
            <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="flex-1">
                    <div className="relative w-full">
                        <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search students..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg w-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button className="px-4 py-2 bg-white/50 text-gray-700 rounded-lg hover:bg-white transition-colors flex items-center gap-2 justify-center">
                        <Download className="w-4 h-4" />
                        Export
                    </button>
                    <button className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center gap-2 justify-center">
                        <UserPlus className="w-4 h-4" />
                        Add Student
                    </button>
                </div>
            </div>
            {editingAcorn ? (
                <AcornEdit acorn={editingAcorn} onSave={handleSave} onCancel={handleCancel} />
            ) : (
                <>
                    {/* Acorns Table */}
                    <table className="min-w-full bg-white border border-gray-200 mb-6">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-4 py-2 border-b">Code</th>
                                <th className="px-4 py-2 border-b">Points</th>
                                <th className="px-4 py-2 border-b">Description</th>
                                <th className="px-4 py-2 border-b">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {acorns.map(acorn => (
                                <tr key={acorn.acornId} className="text-center">
                                    <td className="px-4 py-2 border-b flex items-center space-x-2">
                                        {acorn.code}
                                        <span
                                            onClick={() => {
                                                navigator.clipboard.writeText(acorn.code);
                                                toast.success(
                                                    "Code copied to clipboard", {
                                                });
                                            }}
                                            className="p-1 rounded hover:bg-gray-200"
                                            title="Copy code"
                                        >
                                            <Copy className="w-4 h-4 text-gray-600 hover:text-gray-800" />
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 border-b">{acorn.points}</td>
                                    <td className="px-4 py-2 border-b">{acorn.acornDescription}</td>
                                    <td className="px-4 py-2 border-b space-x-2">
                                        <button
                                            onClick={() => handleEdit(acorn)}
                                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteAcorn(acorn.acornId)}
                                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded"
                                        >
                                            Delete
                                        </button>
                                        <button
                                            onClick={() => reIssueAcorn(acorn.acornId)}
                                            className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1 rounded"
                                        >
                                            ReIssue
                                        </button>
                                        <button
                                            onClick={() => handleGenerateCouponPDF([acorn])} // cast single acorn as tuple
                                            className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded"
                                        >
                                            Download Coupon PDF
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {/* Generate Acorns Form */}
            <div className="bg-white p-4 rounded shadow">
                <h2 className="text-xl font-semibold mb-4">Generate Coupons</h2>
                <table className="min-w-full">
                    <thead>
                        <tr>
                            <th className="px-4 py-2 border-b">Parameter</th>
                            <th className="px-4 py-2 border-b">Input</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="px-4 py-2 border-b">Organization</td>
                            <td className="px-4 py-2 border-b">
                                <select
                                    value={generateParams.OrganizationId}
                                    onChange={e =>
                                        setGenerateParams({
                                            ...generateParams,
                                            OrganizationId: e.target.value,
                                        })
                                    }
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                                >
                                    <option value={0}>Select Organization</option>
                                    {organizations.map(org => (
                                        <option key={org.organizationId} value={org.organizationId}>
                                            {org.organizationName}
                                        </option>
                                    ))}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 border-b">Prefix</td>
                            <td className="px-4 py-2 border-b">
                                <input
                                    type="text"
                                    placeholder="Prefix"
                                    value={generateParams.prefix}
                                    onChange={e =>
                                        setGenerateParams({ ...generateParams, prefix: e.target.value })
                                    }
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 border-b">Points</td>
                            <td className="px-4 py-2 border-b">
                                <input
                                    type="number"
                                    placeholder="Points"
                                    value={generateParams.points}
                                    onChange={e =>
                                        setGenerateParams({ ...generateParams, points: Number(e.target.value) })
                                    }
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 border-b">Number of Acorns</td>
                            <td className="px-4 py-2 border-b">
                                <input
                                    type="number"
                                    placeholder="Number of Acorns"
                                    value={generateParams.numAcorns}
                                    onChange={e =>
                                        setGenerateParams({ ...generateParams, numAcorns: Number(e.target.value) })
                                    }
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 border-b">Description</td>
                            <td className="px-4 py-2 border-b">
                                <input
                                    type="text"
                                    placeholder="Description"
                                    value={generateParams.description}
                                    onChange={e =>
                                        setGenerateParams({ ...generateParams, description: e.target.value })
                                    }
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2} className="px-4 py-2">
                                <button
                                    onClick={generateAcorns}
                                    className="bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded w-full"
                                >
                                    Generate Acorns
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {isReissueOpen && (
                <QRScanner
                    onClose={() => setIsReissueOpen(false)}
                    mode="reissue"
                    onRedeemSuccess={() => {
                        fetchAcorns();
                        setIsReissueOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default AcornMain;
